import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import toast from "react-hot-toast";

import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";

import { getSessions } from "../../services/sessionService";
import { getStaffStatistics } from "../../services/staffStatisticsService";

const { Option } = Select;

const StaffStatsTab = ({ institution }) => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("Not Selected");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  useEffect(() => {
    if (!institution?.id) return;
    loadAll(institution.id);
  }, [institution?.id]);

  const loadAll = async (institutionId) => {
    setLoading(true);
    try {
      const [sessRes, statRes] = await Promise.all([
        getSessions(),
        getStaffStatistics(),
      ]);

      const allSessions = sessRes?.data?.data || [];
      const allStats = statRes?.data?.data || [];

      // scope to this institution
      const scoped = allStats.filter(
        (s) => s.institution?.id === institutionId
      );

      setSessions(allSessions);
      setStats(scoped);
      setFilteredStats(scoped);
    } catch {
      toast.error("Failed to load staff statistics");
    } finally {
      setLoading(false);
    }
  };

  // Filters (search on session name / focus area)
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((row) => {
        const sessionName = (row.session?.name || "").toLowerCase();
        const faName = (row.focus_area?.name || "").toLowerCase();
        return sessionName.includes(term) || faName.includes(term);
      });
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession?.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, selectedSession, stats]);

  // Group by Session (totals across focus areas)
  const groupedStats = useMemo(() => {
    const grouped = new Map();
    for (const item of filteredStats) {
      const sess = item.session;
      if (!sess) continue;

      const key = `${institution.id}__${sess.id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          session: sess,
          data: [],
          totals: {
            phd_full: 0,
            phd_part: 0,
            phd_retained: 0,
            prof_full: 0,
            prof_part: 0,
            prof_retained: 0,
            msc_full: 0,
            msc_part: 0,
            msc_retained: 0,
            total: 0,
          },
        });
      }
      const g = grouped.get(key);
      g.data.push(item);

      const phdF = item.phd_staff_count_full_time || 0;
      const phdP = item.phd_staff_count_part_time || 0;
      const phdR = item.phd_staff_count_retained || 0;

      const profF = item.professor_staff_count_full_time || 0;
      const profP = item.professor_staff_count_part_time || 0;
      const profR = item.professor_staff_count_retained || 0;

      const mscF = item.masters_staff_count_full_time || 0;
      const mscP = item.masters_staff_count_part_time || 0;
      const mscR = item.masters_staff_count_retained || 0;

      g.totals.phd_full += phdF;
      g.totals.phd_part += phdP;
      g.totals.phd_retained += phdR;

      g.totals.prof_full += profF;
      g.totals.prof_part += profP;
      g.totals.prof_retained += profR;

      g.totals.msc_full += mscF;
      g.totals.msc_part += mscP;
      g.totals.msc_retained += mscR;

      g.totals.total +=
        phdF + phdP + phdR + profF + profP + profR + mscF + mscP + mscR;
    }
    return Array.from(grouped.values());
  }, [filteredStats, institution]);

  const columns = [
    { title: "Year", dataIndex: ["session", "name"], width: 140 },
    { title: "PhD FT", dataIndex: ["totals", "phd_full"], width: 100 },
    { title: "PhD PT", dataIndex: ["totals", "phd_part"], width: 100 },
    {
      title: "PhD Retained",
      dataIndex: ["totals", "phd_retained"],
      width: 120,
    },
    { title: "Professor FT", dataIndex: ["totals", "prof_full"], width: 120 },
    { title: "Professor PT", dataIndex: ["totals", "prof_part"], width: 120 },
    {
      title: "Professor Retained",
      dataIndex: ["totals", "prof_retained"],
      width: 150,
    },
    { title: "Masters FT", dataIndex: ["totals", "msc_full"], width: 110 },
    { title: "Masters PT", dataIndex: ["totals", "msc_part"], width: 110 },
    {
      title: "Masters Retained",
      dataIndex: ["totals", "msc_retained"],
      width: 150,
    },
    { title: "Total", dataIndex: ["totals", "total"], width: 120 },
  ];

  return (
    <div className="pt-2">
      <Table
        isPaginable
        pageSize={10}
        isSearchable={false}
        loading={loading}
        columns={columns}
        rows={groupedStats}
        expandable={{
          expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
          onExpand: (expanded, record) => {
            setExpandedRowKey(expanded ? record.key : null);
          },
          expandedRowRender: (record) => (
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Focus Area</th>
                  <th className="text-left p-2">PhD FT</th>
                  <th className="text-left p-2">PhD PT</th>
                  <th className="text-left p-2">PhD Retained</th>
                  <th className="text-left p-2">Professor FT</th>
                  <th className="text-left p-2">Professor PT</th>
                  <th className="text-left p-2">Professor Retained</th>
                  <th className="text-left p-2">Masters FT</th>
                  <th className="text-left p-2">Masters PT</th>
                  <th className="text-left p-2">Masters Retained</th>
                  <th className="text-left p-2">Total</th>
                </tr>
              </thead>
              <tbody className="bg-amber-50">
                {record.data.map((item) => {
                  const rowTotal =
                    (item.phd_staff_count_full_time || 0) +
                    (item.phd_staff_count_part_time || 0) +
                    (item.phd_staff_count_retained || 0) +
                    (item.professor_staff_count_full_time || 0) +
                    (item.professor_staff_count_part_time || 0) +
                    (item.professor_staff_count_retained || 0) +
                    (item.masters_staff_count_full_time || 0) +
                    (item.masters_staff_count_part_time || 0) +
                    (item.masters_staff_count_retained || 0);

                  return (
                    <tr key={item.id}>
                      <td className="p-2 text-sm">
                        {item.focus_area?.name || "N/A"}
                      </td>
                      <td className="p-2 text-sm">
                        {item.phd_staff_count_full_time}
                      </td>
                      <td className="p-2 text-sm">
                        {item.phd_staff_count_part_time}
                      </td>
                      <td className="p-2 text-sm">
                        {item.phd_staff_count_retained}
                      </td>

                      <td className="p-2 text-sm">
                        {item.professor_staff_count_full_time}
                      </td>
                      <td className="p-2 text-sm">
                        {item.professor_staff_count_part_time}
                      </td>
                      <td className="p-2 text-sm">
                        {item.professor_staff_count_retained}
                      </td>

                      <td className="p-2 text-sm">
                        {item.masters_staff_count_full_time}
                      </td>
                      <td className="p-2 text-sm">
                        {item.masters_staff_count_part_time}
                      </td>
                      <td className="p-2 text-sm">
                        {item.masters_staff_count_retained}
                      </td>

                      <td className="p-2 text-sm">{rowTotal}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ),
        }}
        extra={
          <div className="mb-2 py-2 flex gap-2">
            <Select
              multiple={false}
              id="sessions"
              defaultValue={selectedSession}
              className="w-full h-[36px] min-w-[250px]"
              onChange={(value) => setSelectedSession(value)}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value={"Not Selected"}>Filter by Year</Option>
              {sessions.map((s) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </div>
        }
      />
    </div>
  );
};

export default StaffStatsTab;
